import { useCallback, useEffect, useRef, useState } from 'react';

import { Box, Button, Text, useToast } from '@chakra-ui/react';
import { type RenderProps } from 'node_modules/@chakra-ui/react/dist/types/toast/toast.types';
import { useTranslation } from 'react-i18next';
import type Webcam from 'react-webcam';

import { IconAlertTriangle, IconXClose } from '@/components';
import { useCountdown, useLatestValue } from '@/utils/hooks';

export const getSupportedMimeType = () => {
  const mimeTypes = ['video/webm', 'video/mp4'];
  for (const mimeType of mimeTypes) {
    if (MediaRecorder.isTypeSupported(mimeType)) return mimeType;
  }
  return '';
};

type UseVideoRecorderArgs = {
  onCaptureEnd?: () => void;
  durationSec: number;
  preparationSec: number;
  onTechnicalError?: () => void;
};

export type UseVideoRecorderReturn = ReturnType<typeof useVideoRecorder>;

const SILENCE_THRESHOLD = 30;
const SILENCE_PERIOD = 15_000;

export const useVideoRecorder = ({
  onCaptureEnd,
  durationSec = 0,
  preparationSec = 3,
  onTechnicalError,
}: UseVideoRecorderArgs) => {
  const webcamRef = useRef<Webcam>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const audioDataArrayRef = useRef<Uint8Array | null>(null);
  const recordedChunks = useRef<Array<Blob>>([]);
  const onCaptureEndRef = useLatestValue(onCaptureEnd);
  const capturingRef = useRef(false);
  const silentPeriodStart = useRef<number | null>(null);
  const notifiedRef = useRef<boolean>(false);

  const [capturing, setCapturing] = useState(false);
  const [hasVideo, setHasVideo] = useState(false);
  const [isVideoPlayMode, setIsVideoPlayMode] = useState(false);
  const [hasTechnicalIssue, setHasTechnicalIssue] = useState(false);

  const toast = useToast();
  const { t } = useTranslation(['coach']);

  const createBlobUrl = (chunks: Array<Blob>) => {
    const blob = new Blob(chunks, { type: getSupportedMimeType() });
    return URL.createObjectURL(blob);
  };

  const handlePlayVideo = useCallback(() => {
    if (recordedChunks.current.length && videoRef.current) {
      videoRef.current.src = createBlobUrl(recordedChunks.current);
      setIsVideoPlayMode(true);
    }
  }, []);

  const handleStopCapture = useCallback(() => {
    if (mediaRecorderRef.current?.state !== 'inactive') {
      mediaRecorderRef.current?.stop();
    }

    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
      analyserRef.current = null;
      audioDataArrayRef.current = null;
      silentPeriodStart.current = null;
      capturingRef.current = false;
    }
  }, []);

  const [count, { startCountdown, stopCountdown, resetCountdown }] =
    useCountdown({
      countStart: durationSec,
      endOn: 0,
      countStop: -1,
      onEnd: handleStopCapture,
    });

  const stopVideo = useCallback(() => {
    if (!videoRef.current) return;

    videoRef.current?.pause();
    videoRef.current.currentTime = 0;
  }, []);

  const handleDataAvailable = useCallback(({ data }: BlobEvent) => {
    console.log('mediaRecorder.ondataavailable, e.data.size=' + data.size);
    if (data.size > 0) {
      recordedChunks.current.push(data);
      setHasVideo(true);
    }
  }, []);

  const analyzeAudioCheck = useCallback(
    (stream: MediaStream) => {
      if (!audioContextRef.current) {
        audioContextRef.current = new AudioContext();
      }
      const audioContext = audioContextRef.current;

      const source = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();

      analyser.fftSize = 2048;
      const bufferLength = analyser.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);

      analyserRef.current = analyser;
      audioDataArrayRef.current = dataArray;

      source.connect(analyser);

      const analyze = () => {
        if (!analyserRef.current || !audioDataArrayRef.current) return;

        analyser.fftSize = 2048;
        const dataArray = new Uint8Array(analyser.frequencyBinCount);
        source.connect(analyser);

        analyser.getByteFrequencyData(dataArray);
        const sampleRate = audioContext.sampleRate;
        const binSize = sampleRate / analyser.fftSize;
        const startBin = Math.floor(300 / binSize);
        const endBin = Math.ceil(3400 / binSize);
        const voiceFrequencyRange = dataArray.slice(startBin, endBin);

        const averageVoice =
          voiceFrequencyRange.reduce((sum, value) => sum + value, 0) /
          voiceFrequencyRange.length;

        const currentTimestamp = Date.now();
        analyserRef.current.getByteFrequencyData(audioDataArrayRef.current);

        if (averageVoice < SILENCE_THRESHOLD) {
          if (!silentPeriodStart.current) {
            silentPeriodStart.current = currentTimestamp;
          }
        } else {
          silentPeriodStart.current = null;
        }

        if (
          !notifiedRef.current &&
          silentPeriodStart.current &&
          currentTimestamp - silentPeriodStart.current >= SILENCE_PERIOD
        ) {
          if (onTechnicalError) onTechnicalError();
          setHasTechnicalIssue(true);
          notifiedRef.current = true;
          toast({
            render: (props) => (
              <AlertToast
                {...props}
                message={t('coach:technicalProblem.title')}
              />
            ),
            status: 'warning',
            icon: <IconAlertTriangle />,
            id: 'unique-toast',
            variant: 'subtle',
            isClosable: true,
            duration: 3000,
          });
        }

        if (capturingRef.current) {
          requestAnimationFrame(analyze);
        }
      };

      analyze();
    },
    [onTechnicalError, t, toast]
  );

  useEffect(() => {
    if (webcamRef?.current?.stream && capturing) {
      analyzeAudioCheck(webcamRef.current.stream);
    }
  }, [analyzeAudioCheck, webcamRef?.current?.stream, capturing]);

  const handleStartCapture = useCallback(() => {
    if (!webcamRef.current?.stream) return;
    capturingRef.current = true;

    setHasTechnicalIssue(false);
    startCountdown();
    setCapturing(true);
    setIsVideoPlayMode(false);
    stopVideo();

    const mimeType = getSupportedMimeType();

    mediaRecorderRef.current = new MediaRecorder(webcamRef.current.stream!, {
      mimeType: mimeType,
      videoBitsPerSecond: 500000,
    });

    mediaRecorderRef.current.addEventListener(
      'dataavailable',
      handleDataAvailable
    );

    mediaRecorderRef.current.addEventListener('stop', () => {
      setCapturing(false);
      stopCountdown();
      handlePlayVideo();
      onCaptureEndRef.current?.();
    });

    // Start with 1000ms timeslice for incremental chunk generation
    mediaRecorderRef.current.start(1000);
  }, [
    handleDataAvailable,
    handlePlayVideo,
    onCaptureEndRef,
    startCountdown,
    stopCountdown,
    stopVideo,
  ]);

  const [
    initialCount,
    {
      startCountdown: startInitialCountdown,
      resetCountdown: resetInitialCountdown,
      skipCountdownToEnd,
    },
  ] = useCountdown({
    countStart: preparationSec,
    endOn: 0,
    countStop: -1,
    onEnd: handleStartCapture,
  });

  const handleTryAgain = useCallback(() => {
    notifiedRef.current = false;
    recordedChunks.current = [];
    setHasVideo(false);
    stopVideo();
    setIsVideoPlayMode(false);
    resetCountdown();

    resetInitialCountdown();
    startInitialCountdown();
  }, [resetCountdown, resetInitialCountdown, startInitialCountdown, stopVideo]);

  const onSubmit = useCallback((cb: (video: Blob) => void) => {
    const blob = new Blob(recordedChunks.current, {
      type: getSupportedMimeType(),
    });
    cb(blob);
  }, []);

  useEffect(() => {
    startInitialCountdown();
  }, [startInitialCountdown]);

  return {
    webcamRef,
    videoRef,
    capturing,
    hasVideo,
    isVideoPlayMode,
    count,
    initialCount,
    handleStartCapture,
    handleStopCapture,
    handleTryAgain,
    onSubmit,
    resetCountdown,
    skipCountdownToEnd,
    hasTechnicalIssue,
  };
};

type NoiseToastProps = RenderProps & {
  message: string;
};

export const AlertToast: (props: NoiseToastProps) => React.ReactNode = (
  props
) => {
  return (
    <Box
      w={360}
      border="1px solid"
      borderColor="gray.50"
      borderRadius="12"
      bg="white"
      boxShadow="0px 8px 24px 0px rgba(210, 213, 216, 0.60)"
      p={3}
      display="flex"
      alignItems="center"
      gap={3}
      position="relative"
    >
      <Box
        bg="warning.50"
        fontSize="lg"
        borderColor="warning.25"
        borderWidth="5px"
        px={2}
        py={0.5}
        borderRadius="full"
      >
        <IconAlertTriangle fontSize={24} />
      </Box>
      <Text fontWeight="medium" mr={6}>
        {props.message}
      </Text>
      <Button
        onClick={props.onClose}
        position="absolute"
        top="1"
        right="2"
        variant="unstyled"
        w="10"
        boxShadow="none"
      >
        <IconXClose />
      </Button>
    </Box>
  );
};
